import {
    PaginatedDataSheetBusinessEstimates,
    PaginatedDataSheetLogisticsEstimates,
} from '@lune-fe/dapi-models'
import {
    PaginationKeys,
    truncateDateToLocalDate,
    useErrorSnackbar,
    usePaginationState,
} from '@lune-fe/lune-components-lib'
import { ButtonNew, LoadingWrapper, MainLayoutContainerNew, Pagination } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import Box from '@mui/material/Box'
import { AxiosError } from 'axios'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Range } from 'react-date-range'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import FiltersBoxNew from 'components/FiltersBoxNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import { getDataSheetById, PaginatedDataSheetEmpty } from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { SnackbarMessages } from 'SnackbarMessages'
import ImportInfo from 'views/CalculateEmissions/Imports/ImportInfo'
import UpdateStatusDropdown from 'views/CalculateEmissions/Imports/UpdateStatusDropdown'
import {
    DataSheetBusinessTable,
    DataSheetLogisticsTable,
    SelectableDataSheetLogisticsTable,
} from 'views/CalculateEmissions/index'
import NewCalculationDropdown from 'views/CalculateEmissions/NewCalculationDropdown'
import { NavigationSource } from 'views/CalculateEmissions/Shared/BackButtonWithFromState'
import {
    dataSheetTypeConversion,
    DataSheetTypeOrUnknown,
    downloadSheet,
    getDownloadType,
} from 'views/CalculateEmissions/Shared/utils'
import ActiveFiltersChips from 'views/CalculateEmissions/Tables/ActiveFiltersChips'

import EmptyState from './EmptyState'

const ITEMS_PER_PAGE = 50

const DATE_FORMAT = 'MMM DD, YYYY'

// Type guards to avoid type assertions
const isBusinessEstimates = (
    data:
        | PaginatedDataSheetBusinessEstimates
        | PaginatedDataSheetLogisticsEstimates
        | PaginatedDataSheetEmpty,
): data is PaginatedDataSheetBusinessEstimates => {
    return (
        data.data !== null &&
        data.data.length > 0 &&
        'category' in data.data[0] &&
        typeof data.data[0].category === 'string'
    )
}

export type DataSheetInfo = {
    type: DataSheetTypeOrUnknown
    fileName: string | null
    templateName: string | null
    formattedType: string | null
    numberOfErrors: number | null
    processedDate: string | null
    numberOfCalculations: number | null
    accountId: string | null
}

type ImportDetailsProps = {
    autoDownload?: boolean
}

// eslint-disable-next-line complexity
const ImportDetails = ({ autoDownload = false }: ImportDetailsProps) => {
    useTitle('Emissions Calculations per import')
    const { id } = useParams<{ id: string }>()

    const isTestMode = useIsTestMode()
    const navigate = useNavigate()
    const allQueryParams = useLocation().search
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const [paginatedData, setPaginatedData] = useState<
        | PaginatedDataSheetBusinessEstimates
        | PaginatedDataSheetLogisticsEstimates
        | PaginatedDataSheetEmpty
    >()
    const [currentPage, setCurrentPage] = useState(1)
    const { restoreAndClearPaginationState, clearPaginationState } = usePaginationState()

    const pageHistoryRef = useRef<(string | undefined)[]>([undefined])

    const [loading, setLoading] = useState(true)
    const [loadingInitialState, setLoadingInitialState] = useState(true)
    const hasRestoredPaginationRef = useRef(false)
    const restoredPageRef = useRef<number>(1)
    const hasCompletedInitialLoadRef = useRef(false)
    const hasTriggeredDownloadRef = useRef(false)

    const [dataSheetInfo, setDataSheetInfo] = useState<DataSheetInfo>()
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

    // Filters for logistics CSV
    const [selectedShipmentStatuses, setSelectedShipmentStatuses] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<Range>()

    const typesWithShipmentStatus = [
        'lune',
        'lune_logistics_v2',
        'ecotransit',
        'logistics_user_template',
    ]
    const showShipmentStatus =
        dataSheetInfo?.type && typesWithShipmentStatus.includes(dataSheetInfo.type)

    // Check if any filters are currently applied
    const hasFiltersApplied =
        (dateRange?.startDate !== undefined && dateRange.endDate !== undefined) ||
        selectedShipmentStatuses.length > 0

    const assignDataSheetInfo = (
        paginatedResult:
            | PaginatedDataSheetBusinessEstimates
            | PaginatedDataSheetLogisticsEstimates
            | PaginatedDataSheetEmpty,
    ) => {
        const type =
            'type' in paginatedResult && paginatedResult.type ? paginatedResult.type : 'lune'
        const formattedType = dataSheetTypeConversion[type]
        const fileName =
            'filename' in paginatedResult && paginatedResult.filename
                ? paginatedResult.filename
                : null
        const template =
            'name' in paginatedResult && paginatedResult.name ? paginatedResult.name : null
        const numberOfErrors = 'numErrors' in paginatedResult ? paginatedResult.numErrors : null
        const numberOfCalculations =
            'numCalculations' in paginatedResult ? paginatedResult.numCalculations : null
        const processedAt =
            'processedAt' in paginatedResult
                ? moment(paginatedResult.processedAt).format(DATE_FORMAT)
                : null
        const accountId = 'accountId' in paginatedResult ? paginatedResult.accountId : null

        setDataSheetInfo({
            type,
            fileName,
            templateName: template,
            formattedType,
            numberOfErrors,
            numberOfCalculations,
            processedDate: processedAt,
            accountId,
        })
    }

    const loadData = useCallback(
        async (pageNumber: number) => {
            setLoading(true)
            const cursor = pageHistoryRef.current[pageNumber - 1]

            // Determine isShipment parameter based on shipment status filters
            // If only "shipment" is selected, filter to shipment only
            // If only "pre-shipment" is selected, filter to pre-shipment only
            // If both are selected, don't filter (undefined)
            let isShipment: boolean | undefined
            if (selectedShipmentStatuses.length === 1) {
                isShipment =
                    selectedShipmentStatuses[0] === 'shipment'
                        ? true
                        : selectedShipmentStatuses[0] === 'pre-shipment'
                          ? false
                          : undefined
            }

            const result = await getDataSheetById({
                limit: ITEMS_PER_PAGE,
                after: cursor,
                id,
                from: dateRange?.startDate
                    ? truncateDateToLocalDate(dateRange.startDate)
                    : undefined,
                through: dateRange?.endDate
                    ? truncateDateToLocalDate(dateRange.endDate)
                    : undefined,
                isShipment,
            })

            if (result.isOk()) {
                const paginatedResult = result.value
                setPaginatedData(paginatedResult)
                if (
                    paginatedResult.hasMore &&
                    paginatedResult.data &&
                    paginatedResult.nextCursor &&
                    paginatedResult.data.length > 0
                ) {
                    const newHistory = [...pageHistoryRef.current]
                    newHistory[pageNumber] = paginatedResult.nextCursor
                    pageHistoryRef.current = newHistory
                }

                assignDataSheetInfo(paginatedResult)
            } else {
                const error = result.error
                if (
                    error instanceof AxiosError &&
                    error.response &&
                    error.response.status === 404
                ) {
                    navigate('/404')
                } else {
                    snackbar(SnackbarMessages.GENERIC_ERROR, { error })
                    navigate('/imports')
                }
            }
            setLoading(false)
        },
        [snackbar, id, selectedShipmentStatuses, dateRange],
    )

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        loadData(page)
    }

    useEffect(() => {
        // Restore pagination state based on the calculation type
        const savedState = restoreAndClearPaginationState(PaginationKeys.ESTIMATES_PER_IMPORT)
        if (savedState) {
            setCurrentPage(savedState.page)
            pageHistoryRef.current = savedState.cursorHistory
            hasRestoredPaginationRef.current = true
            restoredPageRef.current = savedState.page
        } else {
            restoredPageRef.current = 1
        }
        setLoadingInitialState(false)
        // We only want to trigger this on the initial component render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!loadingInitialState) {
            const isInitialLoad = !hasCompletedInitialLoadRef.current

            if (isInitialLoad) {
                const targetPage = restoredPageRef.current

                // Ensure currentPage state is in sync
                if (currentPage !== targetPage) {
                    setCurrentPage(targetPage)
                }

                hasCompletedInitialLoadRef.current = true
                loadData(targetPage)
            }
        }
        // We want to trigger this only once after initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingInitialState])

    // Reload data when filters change
    useEffect(() => {
        if (hasCompletedInitialLoadRef.current) {
            // Reset pagination and reload from page 1 when filters change
            clearPaginationState(PaginationKeys.ESTIMATES_PER_IMPORT)
            setCurrentPage(1)
            pageHistoryRef.current = [undefined]
            loadData(1)
        }
        // We want to trigger this only when filters change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, selectedShipmentStatuses])

    // Auto-trigger download when accessing the dedicated download route
    useEffect(() => {
        if (
            autoDownload &&
            paginatedData?.type &&
            !hasTriggeredDownloadRef.current &&
            paginatedData.id
        ) {
            downloadSheet(paginatedData.id, getDownloadType(paginatedData.type))
            hasTriggeredDownloadRef.current = true
        }
    }, [autoDownload, paginatedData])

    return (
        <MainLayoutContainerNew
            breadCrumbs={
                dataSheetInfo?.formattedType
                    ? [
                          {
                              label: 'Imports',
                              onClick: () => {
                                  navigate(`/imports${allQueryParams}`)
                              },
                          },
                          {
                              label: dataSheetInfo.formattedType,
                          },
                      ]
                    : undefined
            }
            isTestMode={!!isTestMode}
            title={dataSheetInfo?.formattedType ?? ''}
            moreInfo={dataSheetInfo && <ImportInfo info={dataSheetInfo} />}
            primaryButton={<NewCalculationDropdown from={NavigationSource.IMPORTS} />}
            secondaryButton={
                paginatedData?.type && (
                    <ButtonNew
                        variant={'secondary'}
                        leadingIcon={<FileDownloadOutlinedIcon />}
                        onClick={() =>
                            downloadSheet(
                                paginatedData.id,
                                getDownloadType(paginatedData.type ?? 'unknown'),
                            )
                        }
                    >
                        Download {paginatedData.type === 'rfq' ? 'XLSX' : 'CSV'}
                    </ButtonNew>
                )
            }
            sx={{
                height: isTestMode ? `calc(100vh - 24px)` : '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '.main-layout-container__header': {
                    top: 0,
                },
                '.main-layout-container__body': {
                    overflowY: 'hidden',
                },
            }}
        >
            <WhiteBoxContainer
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <LoadingWrapper
                    sx={{
                        minHeight: '600px',
                    }}
                    loading={!dataSheetInfo}
                >
                    <Box
                        sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 1,
                            }}
                        >
                            {showShipmentStatus &&
                                (paginatedData?.data !== null || hasFiltersApplied) && (
                                    <FiltersBoxNew<string>
                                        preselectedDateRange={dateRange}
                                        setDateRange={setDateRange}
                                        filters={[
                                            {
                                                filterName: 'shipment status',
                                                filterIcon: <LocalShippingOutlinedIcon />,
                                                options: ['shipment', 'pre-shipment'],
                                                preselected: selectedShipmentStatuses,
                                                setSelectedOptions: setSelectedShipmentStatuses,
                                                transformLabel: (label: string) =>
                                                    label.charAt(0).toUpperCase() + label.slice(1),
                                            },
                                        ]}
                                    />
                                )}
                            {paginatedData &&
                                showShipmentStatus &&
                                (paginatedData.data !== null || hasFiltersApplied) && (
                                    <UpdateStatusDropdown
                                        estimateIds={Array.from(selectedIds)}
                                        setTableLoading={(loading) => setLoading(loading)}
                                        onSuccess={() => {
                                            loadData(currentPage)
                                            setSelectedIds(new Set())
                                        }}
                                        sheetId={paginatedData.id}
                                    />
                                )}
                        </Box>
                        {paginatedData &&
                            paginatedData.data !== null &&
                            paginatedData.data.length > 0 &&
                            dataSheetInfo?.numberOfCalculations && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    currentPageItemCount={paginatedData.data.length}
                                    onPageChange={handlePageChange}
                                    loading={loading}
                                    defaultHasNext={paginatedData.hasMore}
                                />
                            )}
                    </Box>
                    {paginatedData && dataSheetInfo && (
                        <>
                            {dataSheetInfo.numberOfCalculations === 0 && !hasFiltersApplied ? (
                                <EmptyState
                                    sheetId={paginatedData.id}
                                    downloadType={getDownloadType(dataSheetInfo.type)}
                                />
                            ) : (
                                <>
                                    {isBusinessEstimates(paginatedData) ? (
                                        <DataSheetBusinessTable
                                            paginatedEstimates={paginatedData}
                                            currentPage={currentPage}
                                            pageHistoryRef={pageHistoryRef}
                                            queryParams={allQueryParams}
                                            loading={loading}
                                        />
                                    ) : (
                                        <>
                                            {showShipmentStatus ? (
                                                <>
                                                    <ActiveFiltersChips<string>
                                                        filters={[
                                                            {
                                                                filterName: 'shipment status',
                                                                selectedValues:
                                                                    selectedShipmentStatuses,
                                                                setSelectedValues:
                                                                    setSelectedShipmentStatuses,
                                                                transformLabel: (label: string) =>
                                                                    label.charAt(0).toUpperCase() +
                                                                    label.slice(1),
                                                            },
                                                        ]}
                                                    />
                                                    <SelectableDataSheetLogisticsTable
                                                        paginatedEstimates={paginatedData}
                                                        currentPage={currentPage}
                                                        pageHistoryRef={pageHistoryRef}
                                                        queryParams={allQueryParams}
                                                        loading={loading}
                                                        selectedIds={selectedIds}
                                                        setSelectedIds={setSelectedIds}
                                                    />
                                                </>
                                            ) : paginatedData.data?.length ? (
                                                <DataSheetLogisticsTable
                                                    paginatedEstimates={paginatedData}
                                                    currentPage={currentPage}
                                                    pageHistoryRef={pageHistoryRef}
                                                    queryParams={allQueryParams}
                                                    loading={loading}
                                                />
                                            ) : null}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </LoadingWrapper>
            </WhiteBoxContainer>
        </MainLayoutContainerNew>
    )
}

export default ImportDetails
